import { Blocks } from "@/components/cms/Blocks";
import { ArrowRight, ButtonLink, TextLink } from "@/components/ui/Button";
import { ProjectCard, StoryCard } from "@/components/ui/Cards";
import { ExternalLink } from "@/components/ui/ExternalLink";
import { MediaImage } from "@/components/ui/MediaImage";
import { Reveal, RevealGroup } from "@/components/ui/Reveal";
import { Section, SectionHeading } from "@/components/ui/Section";
import { VideoBlock } from "@/components/ui/VideoBlock";
import { useContent, useMediaByKey } from "@/content-context";
import type { LinkDoc, SectionDoc, SectionItemDoc } from "@/content";
import { imageReveal } from "@/lib/motion";
import { anchorId } from "@/lib/slug";

/**
 * Renders one CMS section.
 *
 * A closed set of section types, each mapped to a component that already exists
 * in the design system. That is the point of the closed set: a super admin
 * chooses a shape, not a stylesheet, so a page built in the panel comes out
 * looking like the rest of the site rather than like whatever was pasted in.
 *
 * Photographs inside cards sit in a frame of a fixed shape and are cropped to it
 * around their focal point. An editor can upload a wide photograph into a square
 * slot and the grid still lines up, which is the difference between a CMS a
 * non-designer can use and one they can break.
 *
 * A type this component does not know renders nothing rather than throwing. New
 * section types are added here and to the model together; until both exist, an
 * unknown one is invisible instead of taking a public page down.
 */

export const byOrder = <T extends { order?: number }>(rows?: T[]) =>
  (rows ?? []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

/** The cards an editor has not switched off, in their saved order. */
export const visibleItems = (section?: SectionDoc | null): SectionItemDoc[] =>
  byOrder(section?.items).filter((item) => !item.hidden);

/** The links an editor has not switched off, in their saved order. */
export const visibleLinks = (links?: LinkDoc[]): LinkDoc[] =>
  byOrder(links).filter((link) => !link.hidden && link.label && link.href);

/**
 * One fixed link position in a section written for one place.
 *
 * Those sections give each link a role (the main button, the small link under
 * the line), so a link switched off in the panel keeps its position rather than
 * letting the next one slide into a role it was not written for.
 */
export function linkSlot(section: SectionDoc | null | undefined, index: number): LinkDoc | null {
  const link = byOrder(section?.ctas)[index];
  return link && !link.hidden && link.label && link.href ? link : null;
}

export const isExternalLink = (link: { href: string; external?: boolean }) =>
  Boolean(link.external) || /^https?:\/\//i.test(link.href);

/**
 * A focal point somebody actually chose, or undefined.
 *
 * Every section record carries "50% 50%" as its schema default, which would
 * otherwise override the focal point stored on the photograph itself. Centre is
 * treated as "not chosen" so the photograph's own crop wins until an editor
 * picks something different for this placement.
 */
export const chosenFocalPoint = (value?: string) => (value && value !== "50% 50%" ? value : undefined);

export const gridColumns = (columns?: number) => {
  const n = Math.min(Math.max(columns ?? 3, 1), 4);
  return n === 4 ? "sm:grid-cols-2 lg:grid-cols-4" : n === 3 ? "sm:grid-cols-2 lg:grid-cols-3" : n === 2 ? "sm:grid-cols-2" : "";
};

export const headingIdFor = (section: SectionDoc, suffix = "heading") =>
  `${anchorId(section.key ?? String(section._id ?? section.type))}-${suffix}`;

/** A row of buttons: the first is the primary action. External links say where they go. */
export function SectionButtons({
  links,
  light = false,
  className = "mt-8",
}: {
  links: LinkDoc[];
  light?: boolean;
  className?: string;
}) {
  if (!links.length) return null;
  return (
    <div className={`${className} flex flex-col gap-3 sm:flex-row`}>
      {links.map((link, index) =>
        isExternalLink(link) ? (
          <ExternalLink key={link.href + link.label} href={link.href} variant={light ? "buttonLight" : "button"}>
            {link.label}
          </ExternalLink>
        ) : (
          <ButtonLink
            key={link.href + link.label}
            href={link.href}
            variant={index === 0 ? (light ? "light" : "primary") : light ? "ghostLight" : "secondary"}
          >
            {link.label}
            {index === 0 ? <ArrowRight /> : null}
          </ButtonLink>
        ),
      )}
    </div>
  );
}

/** A photograph cropped to a fixed shape around its focal point. */
export function FramedImage({
  mediaKey,
  ratio = "4 / 3",
  sizes = "(min-width: 1024px) 33vw, 100vw",
  focalPoint,
  radius = 16,
  className = "",
  priority = false,
}: {
  mediaKey?: string;
  ratio?: string;
  sizes?: string;
  focalPoint?: string;
  radius?: number;
  className?: string;
  priority?: boolean;
}) {
  const item = useMediaByKey()(mediaKey);
  if (!item) return null;
  if (item.needsConsentCheck && item.consentStatus !== "granted") return null;
  return (
    <div className={`relative w-full overflow-hidden bg-mist ${className}`} style={{ aspectRatio: ratio, borderRadius: radius }}>
      <MediaImage
        item={item}
        fill
        priority={priority}
        sizes={sizes}
        className="object-cover"
        objectPosition={focalPoint || item.objectPosition || "50% 50%"}
      />
    </div>
  );
}

/* ---------------------------------------------------------------- places --- */

/**
 * Places named in HISCUS reporting, as cards. The homepage uses these.
 *
 * Each card links to what happened in that place. A photograph is optional and
 * none is seeded, because a picture on a place card is a claim about where it
 * was taken.
 */
export function LocationCards({ section }: { section: SectionDoc }) {
  const items = visibleItems(section);
  if (!section.title && !items.length) return null;
  const headingId = headingIdFor(section);
  const all = linkSlot(section, 0);

  return (
    <Section tone={section.settings?.tone ?? "white"} ariaLabelledBy={section.title ? headingId : undefined}>
      <div className="flex flex-wrap items-end justify-between gap-8">
        {section.title ? (
          <SectionHeading id={headingId} eyebrow={section.eyebrow} title={section.title} lead={section.lead} />
        ) : null}
        {all ? <TextLink href={all.href}>{all.label}</TextLink> : null}
      </div>

      <RevealGroup as="ul" className={`mt-10 grid gap-5 ${gridColumns(section.settings?.columns)}`}>
        {items.map((item) => (
          <Reveal as="li" key={(item.title ?? "") + (item.href ?? "")}>
            <article className="surface group relative flex h-full flex-col overflow-hidden rounded-[16px]">
              {item.mediaKey ? (
                <FramedImage
                  mediaKey={item.mediaKey}
                  ratio="16 / 10"
                  radius={0}
                  focalPoint={item.focalPoint}
                  sizes="(min-width: 1024px) 31vw, (min-width: 640px) 46vw, 100vw"
                />
              ) : null}
              <div className="flex flex-1 flex-col p-6">
                {item.eyebrow ? (
                  <p className="text-[0.75rem] font-semibold uppercase tracking-[0.14em] text-blue">{item.eyebrow}</p>
                ) : null}
                <h3 className={item.eyebrow ? "mt-2.5" : ""} style={{ fontSize: "var(--step-h4)" }}>
                  {item.title}
                </h3>
                {item.text ? <p className="mt-2.5 text-[0.9375rem] leading-relaxed text-muted">{item.text}</p> : null}
                <Chips value={item.label} />
                {item.href ? (
                  <span className="mt-auto pt-5">
                    <TextLink href={item.href} stretch>
                      {section.texts?.cardLink || `What happened in ${item.title}`}
                    </TextLink>
                  </span>
                ) : null}
              </div>
            </article>
          </Reveal>
        ))}
      </RevealGroup>
    </Section>
  );
}

/**
 * The record by place: each place with the dated work recorded there. The Where
 * we work page uses this. Every place has an anchor, so a homepage card can link
 * straight to it.
 */
export function LocationRecord({ section }: { section: SectionDoc }) {
  const items = visibleItems(section);
  if (!items.length) return null;
  const headingId = headingIdFor(section);
  const after = linkSlot(section, 0);

  return (
    <Section tone={section.settings?.tone ?? "ivory"} ariaLabelledBy={section.title ? headingId : undefined}>
      {section.title ? <SectionHeading id={headingId} eyebrow={section.eyebrow} title={section.title} lead={section.lead} /> : null}

      <RevealGroup as="ul" className="mt-16 space-y-5">
        {items.map((place) => (
          <Reveal as="li" key={place.title}>
            <article id={anchorId(place.title ?? "")} className="surface scroll-mt-28 rounded-[20px] p-7 md:p-9">
              <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)] lg:gap-14">
                <div>
                  {place.mediaKey ? (
                    <FramedImage mediaKey={place.mediaKey} focalPoint={place.focalPoint} className="mb-6" sizes="(min-width: 1024px) 36vw, 100vw" />
                  ) : null}
                  <h3 style={{ fontSize: "var(--step-h3)" }}>{place.title}</h3>
                  {place.eyebrow ? (
                    <p className="mt-1.5 text-[0.8125rem] uppercase tracking-[0.14em] text-blue">{place.eyebrow}</p>
                  ) : null}
                  {place.text ? <p className="mt-5 text-[0.9375rem] leading-relaxed text-muted">{place.text}</p> : null}
                  <Chips value={place.label} className="mt-6" />
                </div>

                {place.facts?.length ? (
                  <dl className="space-y-5">
                    {place.facts.map((entry) => (
                      <div key={entry.label + entry.value} className="border-b border-line pb-5 last:border-0 last:pb-0">
                        <dt className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
                          <span className="font-serif text-[1.0625rem] text-midnight">{entry.label}</span>
                          {entry.date ? <span className="text-[0.8125rem] text-muted">{entry.date}</span> : null}
                        </dt>
                        <dd className="mt-1.5 text-[0.9375rem] leading-relaxed text-muted">{entry.value}</dd>
                      </div>
                    ))}
                  </dl>
                ) : null}
              </div>
            </article>
          </Reveal>
        ))}
      </RevealGroup>

      {after ? (
        <Reveal className="mt-14">
          <TextLink href={after.href}>{after.label}</TextLink>
        </Reveal>
      ) : null}
    </Section>
  );
}

function Chips({ value, className = "mt-5" }: { value?: string; className?: string }) {
  const chips = (value ?? "")
    .split("·")
    .map((part) => part.trim())
    .filter(Boolean);
  if (!chips.length) return null;
  return (
    <ul className={`${className} flex flex-wrap gap-2`}>
      {chips.map((chip) => (
        <li key={chip} className="rounded-full bg-mist px-3 py-1 text-[0.75rem] font-medium text-navy">
          {chip}
        </li>
      ))}
    </ul>
  );
}

/* ----------------------------------------------------- projects, stories --- */

/** Projects from their own collection, filtered to one programme where the section names one. */
export function ProjectListSection({ section, programmeSlug }: { section: SectionDoc; programmeSlug?: string }) {
  const { projects } = useContent();
  const slug = section.refSlug || programmeSlug;
  const list = slug ? projects.filter((project) => project.pillarSlug === slug) : projects;
  if (!list.length) return null;
  const headingId = headingIdFor(section);

  return (
    <Section tone={section.settings?.tone ?? "white"} ariaLabelledBy={section.title ? headingId : undefined}>
      {section.title ? <SectionHeading id={headingId} eyebrow={section.eyebrow} title={section.title} lead={section.lead} /> : null}
      <RevealGroup as="ul" className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {list.map((project) => (
          <Reveal as="li" key={project.slug}>
            <ProjectCard project={project} />
          </Reveal>
        ))}
      </RevealGroup>
    </Section>
  );
}

/** Stories from their own collection, filtered to one programme where the section names one. */
export function StoryListSection({ section, programmeSlug }: { section: SectionDoc; programmeSlug?: string }) {
  const { stories } = useContent();
  const slug = section.refSlug || programmeSlug;
  const list = slug ? stories.filter((story) => story.pillarSlug === slug) : stories;
  if (!list.length) return null;
  const headingId = headingIdFor(section);

  return (
    <Section tone={section.settings?.tone ?? "white"} ariaLabelledBy={section.title ? headingId : undefined}>
      {section.title ? <SectionHeading id={headingId} eyebrow={section.eyebrow} title={section.title} lead={section.lead} /> : null}
      <RevealGroup as="ul" className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {list.map((story) => (
          <Reveal as="li" key={story.slug}>
            <StoryCard story={story} />
          </Reveal>
        ))}
      </RevealGroup>
    </Section>
  );
}

/* ------------------------------------------------------ the generic set --- */

export function SectionRenderer({ section }: { section: SectionDoc }) {
  const byKey = useMediaByKey();
  const tone = section.settings?.tone ?? "white";
  const light = tone === "navy" || tone === "midnight";

  const heading = section.title ? (
    <SectionHeading
      eyebrow={section.eyebrow}
      title={section.title}
      lead={section.lead}
      align={section.settings?.align ?? "left"}
      tone={light ? "light" : "dark"}
    />
  ) : null;

  const actions = <SectionButtons links={visibleLinks(section.ctas)} light={light} />;
  const items = visibleItems(section);

  switch (section.type) {
    case "textBlock":
    case "richText":
      return (
        <Section tone={tone}>
          <Reveal className="max-w-3xl">
            {heading}
            <Blocks blocks={section.body} className={section.title ? "mt-8" : ""} />
            {actions}
          </Reveal>
        </Section>
      );

    case "imageText": {
      const imageFirst = (section.settings?.imageSide ?? "left") === "left";
      return (
        <Section tone={tone}>
          <div className="grid items-start gap-12 lg:grid-cols-2 lg:gap-16">
            <Reveal variants={imageReveal} className={imageFirst ? "" : "lg:order-2"}>
              <FramedImage
                mediaKey={section.mediaKey}
                focalPoint={chosenFocalPoint(section.settings?.focalPoint)}
                radius={20}
                sizes="(min-width: 1024px) 50vw, 100vw"
              />
            </Reveal>
            <Reveal className={imageFirst ? "" : "lg:order-1"}>
              {heading}
              <Blocks blocks={section.body} className={section.title ? "mt-6" : ""} />
              {actions}
            </Reveal>
          </div>
        </Section>
      );
    }

    case "featureCards":
    case "programmeCards":
    case "linkCards":
      return (
        <Section tone={tone}>
          {heading}
          <RevealGroup as="ul" className={`mt-10 grid gap-5 ${gridColumns(section.settings?.columns)}`}>
            {items.map((item) => (
              <Reveal as="li" key={(item.title ?? "") + (item.href ?? "")}>
                <div className="surface group relative flex h-full flex-col overflow-hidden rounded-[16px]">
                  {item.mediaKey ? (
                    <FramedImage mediaKey={item.mediaKey} focalPoint={item.focalPoint} radius={0} sizes="(min-width: 1024px) 30vw, 100vw" />
                  ) : null}
                  <div className="flex flex-1 flex-col p-6">
                    {item.eyebrow ? <p className="eyebrow">{item.eyebrow}</p> : null}
                    <h3 className={item.eyebrow ? "mt-3" : ""} style={{ fontSize: "var(--step-h4)" }}>
                      {item.title}
                    </h3>
                    {item.text ? <p className="mt-2.5 text-[0.9375rem] leading-relaxed text-muted">{item.text}</p> : null}
                    {item.href ? (
                      <span className="mt-auto pt-5">
                        <TextLink href={item.href} stretch>
                          {item.label || "Read more"}
                        </TextLink>
                      </span>
                    ) : null}
                  </div>
                </div>
              </Reveal>
            ))}
          </RevealGroup>
          {actions}
        </Section>
      );

    case "stats":
      return (
        <Section tone={tone}>
          {heading}
          <RevealGroup as="ul" className="mt-10 grid gap-x-10 gap-y-10 sm:grid-cols-2 lg:grid-cols-4">
            {items.map((item) => (
              <Reveal as="li" key={(item.label ?? "") + (item.value ?? "")}>
                <p className={`font-serif text-[2.5rem] leading-none ${light ? "text-white" : "text-navy"}`}>{item.value}</p>
                <p className={`mt-3 font-semibold ${light ? "text-white" : "text-ink"}`}>{item.label}</p>
                {item.text ? <p className={`mt-1.5 text-[0.875rem] ${light ? "text-white/75" : "text-muted"}`}>{item.text}</p> : null}
              </Reveal>
            ))}
          </RevealGroup>
          {actions}
        </Section>
      );

    case "timeline":
      return (
        <Section tone={tone}>
          {heading}
          <RevealGroup as="ol" className="mt-10 space-y-0">
            {items.map((item) => (
              <Reveal as="li" key={(item.date ?? "") + (item.title ?? "")}>
                <div className="grid gap-3 border-l-2 border-line pb-8 pl-6 last:pb-0 sm:grid-cols-[7rem_minmax(0,1fr)] sm:gap-8 sm:pl-8">
                  <p className="font-serif text-[1.75rem] leading-none text-blue">{item.date}</p>
                  <div>
                    <h3 style={{ fontSize: "var(--step-h4)" }}>{item.title}</h3>
                    {item.text ? <p className="mt-2 text-muted">{item.text}</p> : null}
                  </div>
                </div>
              </Reveal>
            ))}
          </RevealGroup>
        </Section>
      );

    case "video":
    case "videoText": {
      const poster = byKey(section.posterMediaKey) ?? byKey(section.mediaKey);
      return (
        <Section tone={tone}>
          <div
            className={
              section.type === "videoText" ? "grid items-start gap-12 lg:grid-cols-2 lg:gap-16" : "mx-auto max-w-4xl"
            }
          >
            {section.type === "videoText" ? (
              <Reveal>
                {heading}
                <Blocks blocks={section.body} className={section.title ? "mt-6" : ""} />
                {actions}
              </Reveal>
            ) : null}
            <Reveal variants={imageReveal}>
              <VideoBlock url={section.videoUrl} poster={poster} title={section.videoTitle || section.title || "Video"} />
            </Reveal>
          </div>
          {section.type === "video" ? <div className="mx-auto max-w-4xl">{actions}</div> : null}
        </Section>
      );
    }

    case "cta":
      return (
        <Section tone={tone === "white" ? "midnight" : tone}>
          <div className="max-w-3xl">
            {section.title ? (
              <SectionHeading
                eyebrow={section.eyebrow}
                title={section.title}
                lead={section.lead}
                tone={tone === "white" || light ? "light" : "dark"}
              />
            ) : null}
            <Blocks blocks={section.body} className={section.title ? "mt-6" : ""} />
            <SectionButtons links={visibleLinks(section.ctas)} light={tone === "white" || light} />
          </div>
        </Section>
      );

    case "faq":
      return (
        <Section tone={tone}>
          {heading}
          <dl className="mt-10 max-w-3xl space-y-7">
            {items.map((item) => (
              <div key={item.title}>
                <dt className="font-serif text-[1.1875rem] text-midnight">{item.title}</dt>
                <dd className="mt-2 text-muted">{item.text}</dd>
              </div>
            ))}
          </dl>
        </Section>
      );

    case "photoGallery":
      return (
        <Section tone={tone}>
          {heading}
          <RevealGroup
            as="ul"
            className="mt-10 columns-1 gap-5 sm:columns-2 lg:columns-3 [&>li]:mb-5 [&>li]:break-inside-avoid"
          >
            {(section.mediaKeys ?? []).map((key) => {
              const item = byKey(key);
              if (!item) return null;
              return (
                <Reveal as="li" key={key}>
                  <figure>
                    <MediaImage
                      item={item}
                      sizes="(min-width: 1024px) 31vw, (min-width: 640px) 46vw, 100vw"
                      className="w-full rounded-[16px]"
                      style={{ height: "auto" }}
                    />
                    {item.caption ? (
                      <figcaption className="mt-2.5 text-[0.875rem] text-muted">{item.caption}</figcaption>
                    ) : null}
                  </figure>
                </Reveal>
              );
            })}
          </RevealGroup>
          {actions}
        </Section>
      );

    case "locations":
      return <LocationCards section={section} />;

    case "projectList":
      return <ProjectListSection section={section} />;

    case "storyList":
      return <StoryListSection section={section} />;

    /*
     * Team. Renders nothing at all while there is nobody to name.
     *
     * A card saying "leadership is being confirmed" tells a visitor about our
     * content process rather than about HISCUS, and a placeholder person is a
     * fabrication. The section comes back with real people in it.
     */
    case "team":
      if (items.length === 0) return null;
      return (
        <Section tone={tone}>
          {heading}
          <RevealGroup as="ul" className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((item) => (
              <Reveal as="li" key={item.title}>
                <div className="surface h-full overflow-hidden rounded-[16px]">
                  {item.mediaKey ? (
                    <FramedImage mediaKey={item.mediaKey} focalPoint={item.focalPoint} ratio="4 / 5" radius={0} sizes="(min-width: 1024px) 30vw, 100vw" />
                  ) : null}
                  <div className="p-6">
                    <h3 style={{ fontSize: "var(--step-h4)" }}>{item.title}</h3>
                    {item.label ? <p className="mt-1 text-[0.9375rem] text-blue">{item.label}</p> : null}
                    {item.text ? <p className="mt-3 text-[0.9375rem] text-muted">{item.text}</p> : null}
                  </div>
                </div>
              </Reveal>
            ))}
          </RevealGroup>
        </Section>
      );

    /*
     * Testimonials, only when verified.
     *
     * An entry with no attributed source is dropped. An unattributed quote on an
     * organisation's own website is a testimonial the organisation wrote about
     * itself, and this project does not publish those.
     */
    case "testimonials": {
      const sourced = items.filter((item) => item.label || item.source);
      if (sourced.length === 0) return null;
      return (
        <Section tone={tone}>
          {heading}
          <RevealGroup as="ul" className="mt-10 grid gap-6 md:grid-cols-2">
            {sourced.map((item) => (
              <Reveal as="li" key={item.text}>
                <figure className="surface h-full rounded-[16px] p-6">
                  <blockquote className="font-serif text-[1.125rem] leading-snug text-midnight">{item.text}</blockquote>
                  <figcaption className="mt-4 text-[0.875rem] text-muted">{item.label ?? item.source}</figcaption>
                </figure>
              </Reveal>
            ))}
          </RevealGroup>
        </Section>
      );
    }

    default:
      return null;
  }
}
